import { create } from 'zustand';
import type { TileSize } from '../types';

// --- Board shell overlays (SC-2872) — the per-viewer layer OVER a board spec ---
//
// The two-producers-one-schema rule made concrete: the agent writes the SPEC
// (tile sizes, params defaults); the viewer's sizer / dismiss / param-toggle
// gestures write THIS overlay — the spec itself never mutates, so board resets
// and newly-shipped spec changes always merge cleanly (a shell only ever names
// tile ids it has an opinion about).
//
// Keyed by `catalogId:boardId`. Session-scoped module memory for now — the
// durable home is the server-side shell (SC-2870, per user + catalog); when that
// lands, this store hydrates from / writes through to it and the components
// don't change. Registered with `clearSessionState` (SC-2461) so a new identity
// can't inherit the previous one's tile arrangement.

export interface BoardShell {
  /** Per-tile size overrides (only tiles the viewer resized appear). */
  size: Record<string, TileSize>;
  /** Dismissed tile ids, restorable via the board's "Removed" chips. */
  dismissed: string[];
  /** Per-param selections (only params the viewer toggled appear); the spec's
   *  declared default fills the rest at bind time. */
  params: Record<string, string>;
}

export const EMPTY_BOARD_SHELL: BoardShell = { size: {}, dismissed: [], params: {} };

interface BoardShellStore {
  byBoard: Record<string, BoardShell>;
  setTileSize: (catalogId: string, boardId: string, tileId: string, size: TileSize) => void;
  dismissTile: (catalogId: string, boardId: string, tileId: string) => void;
  restoreTile: (catalogId: string, boardId: string, tileId: string) => void;
  setParam: (catalogId: string, boardId: string, param: string, value: string) => void;
  /** Wipe all shells on an identity change (`clearSessionState`, SC-2461). */
  reset: () => void;
}

// Collision-proof composite key — the repo's NUL-separator convention (see
// `cellKey`/`KEY_SEP` in findingsOverlay.ts): a printable delimiter could
// legally appear inside an id and silently alias two distinct (catalog, board)
// pairs onto one shell.
const KEY_SEP = String.fromCharCode(0);

export const boardShellKey = (catalogId: string, boardId: string): string =>
  `${catalogId}${KEY_SEP}${boardId}`;

function withShell(
  state: BoardShellStore,
  key: string,
  patch: (shell: BoardShell) => Partial<BoardShell>,
): Pick<BoardShellStore, 'byBoard'> {
  const shell = state.byBoard[key] ?? EMPTY_BOARD_SHELL;
  return { byBoard: { ...state.byBoard, [key]: { ...shell, ...patch(shell) } } };
}

export const useBoardShellStore = create<BoardShellStore>((set) => ({
  byBoard: {},

  setTileSize: (catalogId, boardId, tileId, size) =>
    set((state) =>
      withShell(state, boardShellKey(catalogId, boardId), (shell) => ({
        size: { ...shell.size, [tileId]: size },
      })),
    ),

  dismissTile: (catalogId, boardId, tileId) =>
    set((state) =>
      withShell(state, boardShellKey(catalogId, boardId), (shell) => ({
        dismissed: shell.dismissed.includes(tileId)
          ? shell.dismissed
          : [...shell.dismissed, tileId],
      })),
    ),

  restoreTile: (catalogId, boardId, tileId) =>
    set((state) =>
      withShell(state, boardShellKey(catalogId, boardId), (shell) => ({
        dismissed: shell.dismissed.filter((id) => id !== tileId),
      })),
    ),

  setParam: (catalogId, boardId, param, value) =>
    set((state) =>
      withShell(state, boardShellKey(catalogId, boardId), (shell) => ({
        params: { ...shell.params, [param]: value },
      })),
    ),

  reset: () => set({ byBoard: {} }),
}));
